import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';

import AdminProtectedRoute from '../../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../../components/admin/AdminLayout';
import StreamBuilder from '../../../components/common/StreamBuilder';
import { BlocManagementRoles } from '../../../bloc/BlocManagementRoles';
import { Role } from '../../../../types/cms.types';
import { CpnRoleForm } from './components/CpnRoleForm';
import { useAdmin } from '../../../../base/AdminContext';

export const RolesManagementPage: React.FC = () => {
    // Admin context for permission checking
    const { checkPermission } = useAdmin();

    // Bloc instance
    const [bloc] = useState(() => new BlocManagementRoles());

    // Permission checks
    const canAddRole = checkPermission('permission_management', 'role_management', 'add');
    const canEditRole = checkPermission('permission_management', 'role_management', 'edit');
    const canDeleteRole = checkPermission('permission_management', 'role_management', 'delete');
    const canInitializeRoles = checkPermission('permission_management', 'role_management', 'add'); // Same as add permission

    // UI state
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [lazyLoading, setLazyLoading] = useState<boolean>(false);

    // Dialog state
    const [showFormDialog, setShowFormDialog] = useState<boolean>(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [formLoading, setFormLoading] = useState<boolean>(false);

    // Refs
    const toast = useRef<Toast>(null);

    // Load data on component mount
    useEffect(() => {
        bloc.initData();

        // Cleanup on unmount
        return () => {
            bloc.dispose();
        };
    }, [bloc]);

    // Search handler
    const handleSearch = async (term: string) => {
        setSearchTerm(term);
        await bloc.search(term);
    };

    // Clear filters
    const clearFilters = async () => {
        setSearchTerm('');
        await bloc.clearFilters();
    };

    // Page change handler for DataTable's onPage event
    const onPageChange = async (event: any) => {
        setLazyLoading(true);

        // If rows changed, reset to page 1
        if (event.rows !== bloc.currentPagination.limit) {
            await bloc.changePageSize(event.rows);
        } else {
            // DataTable's page is 0-based, but our API expects 1-based
            await bloc.changePage(event.page + 1);
        }

        setLazyLoading(false);
    };

    // Role form handlers
    const openCreateDialog = () => {
        if (!canAddRole) {
            console.warn('Không có quyền tạo vai trò');
            return;
        }
        setEditingRole(null);
        setShowFormDialog(true);
    };

    const openEditDialog = (role: Role) => {
        if (!canEditRole) {
            console.warn('Không có quyền sửa vai trò');
            return;
        }
        setEditingRole(role);
        setShowFormDialog(true);
    };

    const closeFormDialog = () => {
        setShowFormDialog(false);
        setEditingRole(null);
        setFormLoading(false);
    };

    const handleFormSubmit = async (roleData: any) => {
        try {
            setFormLoading(true);

            if (editingRole) {
                // Update existing role
                if (!canEditRole) {
                    throw new Error('Không có quyền sửa vai trò');
                }
                await bloc.updateRole({
                    id: editingRole.id!,
                    ...roleData
                });
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Cập nhật vai trò thành công',
                    life: 3000
                });
            } else {
                // Create new role
                if (!canAddRole) {
                    throw new Error('Không có quyền tạo vai trò');
                }
                await bloc.createRole(roleData);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Tạo vai trò mới thành công',
                    life: 3000
                });
            }

            closeFormDialog();
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error instanceof Error ? error.message : 'Có lỗi xảy ra',
                life: 5000
            });
        } finally {
            setFormLoading(false);
        }
    };

    const handleCreateFromTemplate = async (templateName: string) => {
        if (!canAddRole) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: 'Không có quyền tạo vai trò',
                life: 5000
            });
            return;
        }

        try {
            setFormLoading(true);
            await bloc.createRoleFromTemplate(templateName);
            toast.current?.show({
                severity: 'success',
                summary: 'Thành công',
                detail: `Tạo vai trò từ mẫu "${templateName}" thành công`,
                life: 3000
            });
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error instanceof Error ? error.message : 'Có lỗi xảy ra',
                life: 5000
            });
        } finally {
            setFormLoading(false);
        }
    };

    // Delete role handler
    const handleDeleteRole = (role: Role) => {
        if (!canDeleteRole) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: 'Không có quyền xóa vai trò',
                life: 5000
            });
            return;
        }

        confirmDialog({
            message: `Bạn có chắc chắn muốn xóa vai trò "${role.name}" không? Hành động này không thể hoàn tác.`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: 'Xóa',
            rejectLabel: 'Hủy',
            accept: async () => {
                try {
                    await bloc.deleteRole(role.id!);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Xóa vai trò thành công',
                        life: 3000
                    });
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error instanceof Error ? error.message : 'Có lỗi xảy ra khi xóa vai trò',
                        life: 5000
                    });
                }
            }
        });
    };

    // Initialize default roles
    const handleInitializeDefaultRoles = async () => {
        if (!canInitializeRoles) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: 'Không có quyền khởi tạo vai trò',
                life: 5000
            });
            return;
        }

        confirmDialog({
            message: 'Bạn có muốn khởi tạo các vai trò mặc định không? Hành động này sẽ tạo các vai trò hệ thống cơ bản.',
            header: 'Khởi tạo vai trò mặc định',
            icon: 'pi pi-question-circle',
            acceptLabel: 'Khởi tạo',
            rejectLabel: 'Hủy',
            accept: async () => {
                try {
                    await bloc.initializeDefaultRoles();
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Khởi tạo vai trò mặc định thành công',
                        life: 3000
                    });
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error instanceof Error ? error.message : 'Có lỗi xảy ra',
                        life: 5000
                    });
                }
            }
        });
    };

    // Column renderers
    const renderNameColumn = (rowData: Role) => (
        <div>
            <div className="font-bold text-900">{rowData.name}</div>
            {rowData.description && (
                <div className="text-sm text-500 mt-1">{rowData.description}</div>
            )}
        </div>
    );

    const renderPermissionsColumn = (rowData: Role) => (
        <div className="flex flex-wrap gap-1">
            {rowData.permissions.slice(0, 3).map((permission, index) => (
                <Tag
                    key={index}
                    value={permission.module_name}
                    severity="info"
                    className="text-xs"
                />
            ))}
            {rowData.permissions.length > 3 && (
                <Tag
                    value={`+${rowData.permissions.length - 3} khác`}
                    severity="info"
                    className="text-xs"
                />
            )}
        </div>
    );

    const renderPermissionCountColumn = (rowData: Role) => (
        <Tag value={rowData.permissions.length} severity="success" />
    );

    const renderActionColumn = (rowData: Role) => (
        <div className="flex gap-2">
            {canEditRole && (
                <>
                    <Button
                        icon="pi pi-eye"
                        size="small"
                        severity="info"
                        outlined
                        tooltip="Xem chi tiết"
                        onClick={() => openEditDialog(rowData)}
                    />
                    <Button
                        icon="pi pi-pencil"
                        size="small"
                        severity="warning"
                        outlined
                        tooltip="Sửa vai trò"
                        onClick={() => openEditDialog(rowData)}
                    />
                </>
            )}
            {canDeleteRole && (
                <Button
                    icon="pi pi-trash"
                    size="small"
                    severity="danger"
                    outlined
                    tooltip="Xóa vai trò"
                    onClick={() => handleDeleteRole(rowData)}
                />
            )}
            {!canEditRole && !canDeleteRole && (
                <span className="text-400 text-sm italic">Không có quyền</span>
            )}
        </div>
    );

    return (
        <AdminProtectedRoute
            requiredModule="permission_management"
            requiredSubModule="role_management"
            requiredPermission="view"
        >
            <AdminLayout>
                <div className="role-management-page">
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    <div className="card">
                        <div className="flex justify-content-between align-items-center mb-4">
                            <h2 className="text-2xl font-bold text-900 m-0">Quản lý Vai trò</h2>
                            <div className="flex gap-2">
                                {canInitializeRoles && (
                                    <Button
                                        label="Khởi tạo vai trò mặc định"
                                        icon="pi pi-cog"
                                        severity="secondary"
                                        outlined
                                        onClick={handleInitializeDefaultRoles}
                                    />
                                )}
                                {canAddRole && (
                                    <Button
                                        label="Tạo vai trò mới"
                                        icon="pi pi-plus"
                                        onClick={openCreateDialog}
                                        className="p-button-primary"
                                    />
                                )}
                            </div>
                        </div>

                        <StreamBuilder
                            stream={bloc.getStream('info')}
                            initialData={bloc.info}
                            builder={(snapshot: any) => {
                                const data = snapshot?.data || bloc.info;

                                if (!data) {
                                    return <div>Loading...</div>;
                                }

                                return (
                                    <>
                                        {/* Filters */}
                                        <div className="grid mb-4">
                                            <div className="col-12 md:col-6">
                                                <label className="block text-900 font-medium mb-2">Tìm kiếm</label>
                                                <InputText
                                                    value={searchTerm}
                                                    onChange={(e) => handleSearch(e.target.value)}
                                                    placeholder="Tìm kiếm vai trò..."
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <label className="block text-900 font-medium mb-2">&nbsp;</label>
                                                <Button
                                                    label="Xóa bộ lọc"
                                                    icon="pi pi-filter-slash"
                                                    severity="secondary"
                                                    outlined
                                                    onClick={clearFilters}
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>

                                        {/* Stats Cards */}
                                        <div className="grid mb-4">
                                            <div className="col-12 md:col-4">
                                                <Card>
                                                    <div className="flex align-items-center">
                                                        <div className="flex-1">
                                                            <div className="text-500 font-medium mb-1">Tổng vai trò</div>
                                                            <div className="text-2xl font-bold text-900">{data.pagination.total}</div>
                                                        </div>
                                                        <div className="bg-blue-100 text-blue-800 p-3 border-round">
                                                            <i className="pi pi-users text-xl"></i>
                                                        </div>
                                                    </div>
                                                </Card>
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <Card>
                                                    <div className="flex align-items-center">
                                                        <div className="flex-1">
                                                            <div className="text-500 font-medium mb-1">Modules khả dụng</div>
                                                            <div className="text-2xl font-bold text-900">{data.availableModules.length}</div>
                                                        </div>
                                                        <div className="bg-green-100 text-green-800 p-3 border-round">
                                                            <i className="pi pi-th-large text-xl"></i>
                                                        </div>
                                                    </div>
                                                </Card>
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <Card>
                                                    <div className="flex align-items-center">
                                                        <div className="flex-1">
                                                            <div className="text-500 font-medium mb-1">Mẫu có sẵn</div>
                                                            <div className="text-2xl font-bold text-900">{data.availableTemplates.length}</div>
                                                        </div>
                                                        <div className="bg-orange-100 text-orange-800 p-3 border-round">
                                                            <i className="pi pi-clone text-xl"></i>
                                                        </div>
                                                    </div>
                                                </Card>
                                            </div>
                                        </div>

                                        {/* Data Table */}
                                        <DataTable
                                            scrollHeight='calc(100vh - 200px)'
                                            value={data.roles}
                                            loading={data.loading || lazyLoading}
                                            paginator
                                            lazy
                                            first={(data.pagination.page - 1) * data.pagination.limit}
                                            rows={data.pagination.limit}
                                            totalRecords={data.pagination.total}
                                            onPage={onPageChange}
                                            rowsPerPageOptions={[5, 10, 25, 50]}
                                            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                            currentPageReportTemplate="Hiển thị {first} đến {last} trong tổng {totalRecords} vai trò"
                                            emptyMessage="Không có vai trò nào được tìm thấy"
                                            className="p-datatable-gridlines"
                                            rowHover
                                            stripedRows
                                        >
                                            <Column
                                                field="name"
                                                header="Tên vai trò"
                                                body={renderNameColumn}
                                                sortable
                                                style={{ minWidth: '200px' }}
                                            />
                                            <Column
                                                header="Quyền hạn"
                                                body={renderPermissionsColumn}
                                                style={{ minWidth: '200px' }}
                                            />
                                            <Column
                                                header="Số quyền"
                                                body={renderPermissionCountColumn}
                                                style={{ minWidth: '100px' }}
                                            />
                                            <Column
                                                field="created_at"
                                                header="Ngày tạo"
                                                sortable
                                                body={(rowData) => new Date(rowData.created_at).toLocaleDateString('vi-VN')}
                                                style={{ minWidth: '120px' }}
                                            />
                                            {(canEditRole || canDeleteRole) && (
                                                <Column
                                                    header="Thao tác"
                                                    body={renderActionColumn}
                                                    exportable={false}
                                                    style={{ minWidth: '150px' }}
                                                />
                                            )}
                                        </DataTable>
                                    </>
                                );
                            }}
                        />
                    </div>

                    {/* Role Form Dialog */}
                    {showFormDialog && ((editingRole && canEditRole) || (!editingRole && canAddRole)) && (
                        <Dialog
                            header={editingRole ? 'Sửa Vai trò' : 'Tạo Vai trò Mới'}
                            visible={showFormDialog}
                            onHide={closeFormDialog}
                            style={{
                                width: '95vw',
                                maxWidth: '1400px',
                                height: '80vh',
                                maxHeight: '80vh'
                            }}
                            modal
                            className="p-fluid role-dialog"
                            maximizable
                            contentStyle={{
                                padding: '1rem',
                                height: 'calc(80vh - 120px)',
                                overflowY: 'auto'
                            }}
                        >
                            <CpnRoleForm
                                role={editingRole}
                                onSubmit={handleFormSubmit}
                                onCancel={closeFormDialog}
                                loading={formLoading}
                                availableModules={bloc.availableModules}
                                availableTemplates={bloc.availableTemplates}
                                onCreateFromTemplate={handleCreateFromTemplate}
                            />
                        </Dialog>
                    )}
                </div>

                <style>{`
                    /* Responsive dialog for smaller screens */
                    @media (max-height: 900px) {
                        .role-dialog .p-dialog {
                            height: 75vh !important;
                            max-height: 75vh !important;
                        }
                        
                        .role-dialog .p-dialog-content {
                            height: calc(75vh - 130px) !important;
                            padding: 0.75rem !important;
                        }
                    }
                    
                    @media (max-height: 800px) {
                        .role-dialog .p-dialog {
                            height: 70vh !important;
                            max-height: 70vh !important;
                        }
                        
                        .role-dialog .p-dialog-content {
                            height: calc(70vh - 120px) !important;
                            padding: 0.5rem !important;
                        }
                    }
                    
                    @media (max-height: 700px) {
                        .role-dialog .p-dialog {
                            height: 65vh !important;
                            max-height: 65vh !important;
                        }
                        
                        .role-dialog .p-dialog-content {
                            height: calc(65vh - 110px) !important;
                            padding: 0.5rem !important;
                        }
                    }
                    
                    /* Ensure buttons are always visible */
                    .role-dialog .p-dialog-footer {
                        padding: 0.75rem 1rem;
                        border-top: 1px solid var(--surface-border);
                        background: var(--surface-0);
                        position: sticky;
                        bottom: 0;
                        z-index: 1000;
                    }
                    
                    /* Form adjustments for smaller screens */
                    @media (max-width: 1024px) {
                        .role-dialog .p-dialog {
                            width: 98vw !important;
                        }
                    }
                    
                    /* Specific adjustment for 13-inch screens (typically 1440x900 or 1280x800) */
                    @media (min-width: 1200px) and (max-height: 950px) {
                        .role-dialog .p-dialog {
                            height: 72vh !important;
                            max-height: 72vh !important;
                        }
                        
                        .role-dialog .p-dialog-content {
                            height: calc(72vh - 125px) !important;
                        }
                    }
                `}</style>
            </AdminLayout>
        </AdminProtectedRoute>
    );
};
